import { AREAS, LEVELS } from "@/lib/heat";
import { RATING_SCALE } from "@/lib/topics";
import { ASSESSMENT_URL } from "@/components/ResourceNotice";
import { BOOKING_URL } from "@/components/CoachInvite";

const ranges = ["0–11", "12–20", "21–28", "29–36"];

const Lines = ({ n, gap = "h-7" }) => (
  <div>{Array.from({ length: n }).map((_, i) => <div key={i} className={`${gap} border-b border-slate-300`} />)}</div>
);

const Blank = ({ label, w = "flex-1" }) => (
  <div className={`flex items-end gap-2 text-xs text-slate-600 ${w}`}>
    <span className="whitespace-nowrap font-semibold">{label}</span>
    <span className="h-5 flex-1 border-b border-slate-400" />
  </div>
);

const Head = ({ eyebrow, title, children }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">{eyebrow}</div>
    <h2 className="font-display text-2xl font-bold text-slate-900">{title}</h2>
    {children && <p className="mt-1 text-xs leading-relaxed text-slate-600">{children}</p>}
  </div>
);

const Ladder = () => (
  <ol className="space-y-1 text-xs text-slate-700">
    {RATING_SCALE.map((r, i) => (
      <li key={r.short} className="flex items-center gap-2">
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#1F4266]" />
        {r.full}
        {i < 4 && <span className="text-slate-400">→ next level: {RATING_SCALE[i + 1].short}</span>}
      </li>
    ))}
  </ol>
);

export const GuidePage = () => (
  <div data-testid="pk-grow-guide">
    <Head eyebrow="Workbook Key · Grow & Track" title="Choosing Your Focus">
      Use your scores from Section 2 to pick up to three topics to grow in over the next three months. You can keep one goal all three months, or choose a different one each month.
    </Head>
    <div className="mt-4 rounded-lg border border-slate-200 p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Step 1 · My two lowest topics in each area</div>
      <table className="mt-2 w-full border-collapse text-xs">
        <thead>
          <tr className="bg-slate-100 text-left">
            {["Area", "Topic", "My rating", "Next-level rating"].map((h) => <th key={h} className="border border-slate-300 px-2 py-1">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {AREAS.flatMap((a) => [0, 1].map((k) => (
            <tr key={`${a.key}-${k}`}>
              <td className="border border-slate-300 px-2 py-1.5 font-medium">{k === 0 ? a.label : ""}</td>
              <td className="border border-slate-300 px-2 py-1.5" />
              <td className="border border-slate-300 px-2 py-1.5" />
              <td className="border border-slate-300 px-2 py-1.5" />
            </tr>
          )))}
        </tbody>
      </table>
      <p className="mt-1 text-[10px] text-slate-500">If a topic is among the lowest in two areas, list it once and move to the next lowest.</p>
    </div>
    <div className="mt-4 grid grid-cols-[1.4fr_1fr] gap-4">
      <div className="rounded-lg border border-slate-200 p-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Step 2 · Rating ladder (lowest to highest)</div>
        <div className="mt-2"><Ladder /></div>
      </div>
      <div className="rounded-lg border border-slate-200 p-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Area levels</div>
        <ul className="mt-2 space-y-1 text-xs text-slate-700">
          {LEVELS.map((l, i) => (
            <li key={l.label} className="flex items-center gap-2"><span className="inline-block h-2.5 w-6 rounded" style={{ background: l.hex }} />{l.label}: {ranges[i]}</li>
          ))}
        </ul>
      </div>
    </div>
    <div className="mt-4 rounded-lg bg-slate-50 p-3 text-xs leading-relaxed text-slate-700">
      <span className="font-semibold">Step 3 · Write your plan.</span> On each month's Growth Plan page, name the topic, your current rating and the next-level rating you are aiming for, then describe in one paragraph how you will grow. Use the Daily Tracker to mark each day, and finish with Looking Back & Next Step Planner.
    </div>
  </div>
);

export const PlanPage = ({ m }) => (
  <div data-testid={`pk-plan-month-${m}`}>
    <Head eyebrow="Growth Plan Worksheet" title={`Month ${m}`} />
    <div className="mt-4 flex gap-6"><Blank label="Start date:" /><Blank label="End date:" /></div>
    {m > 1 && <div className="mt-3 flex items-center gap-2 text-xs text-slate-600"><span className="inline-block h-3.5 w-3.5 rounded-sm border border-slate-500" /> Same goal as last month</div>}
    <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4">
      <Blank label="Topic of focus:" /><Blank label="Area:" />
      <Blank label="My current rating:" /><Blank label="Next-level rating:" />
    </div>
    <div className="mt-6 text-[11px] font-semibold uppercase tracking-wide text-slate-500">My plan to grow (one paragraph)</div>
    <Lines n={10} />
    <div className="mt-6 text-[11px] font-semibold uppercase tracking-wide text-slate-500">How I'll know I'm growing</div>
    <Lines n={4} />
    <div className="mt-6 rounded-lg border border-slate-200 p-3">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Quick reference · rating ladder</div>
      <Ladder />
    </div>
  </div>
);

const Circles = () => (
  <div className="flex gap-1">{[1, 2, 3, 4, 5].map((v) => <span key={v} className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-400 text-[9px] text-slate-500">{v}</span>)}</div>
);

export const TrackerPage = ({ m, part }) => {
  const days = part === 1 ? [1, 16] : [17, 31];
  const rows = Array.from({ length: days[1] - days[0] + 1 }, (_, i) => days[0] + i);
  return (
    <div data-testid={`pk-tracker-${m}-${part}`}>
      <Head eyebrow="Daily Tracker Worksheet" title={`Month ${m} · Days ${days[0]}–${days[1]}`}>
        Write the date, rate how it went (1 = hard, 5 = great), and note the step you took toward this month's goal.
      </Head>
      <div className="mt-3 grid grid-cols-[3.2rem_5.5rem_7rem_1fr] gap-3 border-b border-slate-300 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        <span>Day</span><span>Date</span><span>How it went</span><span>What step did I take today to reach my goal?</span>
      </div>
      {rows.map((d) => (
        <div key={d} className="grid h-[0.47in] grid-cols-[3.2rem_5.5rem_7rem_1fr] items-end gap-3 border-b border-slate-200 pb-1.5">
          <span className="text-xs font-semibold text-slate-700">Day {d}</span>
          <span className="h-5 border-b border-slate-400" />
          <Circles />
          <span className="h-5 border-b border-slate-400" />
        </div>
      ))}
    </div>
  );
};

const Field = ({ label, hint, n = 2 }) => (
  <div className="mt-3">
    <div className="text-xs font-semibold text-slate-800">{label} <span className="font-normal text-slate-500">{hint}</span></div>
    <Lines n={n} gap="h-6" />
  </div>
);

export const FinalPage = () => (
  <div data-testid="pk-final">
    <Head eyebrow="Looking Back & Next Step Planner" title="Looking Back on My Three Months" />
    <Field label="What did I do?" hint="(activities and practices)" />
    <Field label="What did my tracking show?" />
    <Field label="What did I learn?" />
    <div className="mt-4 rounded-lg border-l-4 border-[#1F4266] bg-slate-50 p-3 text-xs text-slate-700">
      <span className="font-semibold">Time to retake the assessment.</span> Goal date (three months after your start date): ____________________ · Online: {ASSESSMENT_URL}
    </div>
    <div className="mt-4 rounded-lg border border-slate-300 p-3">
      <div className="font-display text-base font-bold text-slate-900">Next Step Planner</div>
      <p className="text-[11px] text-slate-600">A 15–20 minute activity. Complete this worksheet page on your own or with a coach.</p>
      <Field label="My next step" hint="(small and clear action)" n={1} />
      <Field label="When I will do it" hint="(date and time)" n={1} />
      <Field label="Support I need" n={1} />
      <Field label="Likely obstacle(s)" n={1} />
      <Field label="My grace plan" hint="(what I will do if I stumble)" n={2} />
    </div>
    <div className="mt-4 flex items-center gap-3 rounded-lg bg-[#1F4266]/5 p-3">
      <img src="/schedule-qr.png" alt="QR code to schedule a coaching call" className="h-16 w-16" />
      <div className="text-xs text-slate-700"><span className="font-semibold">Want one-on-one support?</span> Scan to schedule a coaching call. {BOOKING_URL}</div>
    </div>
  </div>
);

export const NotesPage = () => (
  <div><Head eyebrow="Workbook" title="Notes" /><div className="mt-4"><Lines n={30} /></div></div>
);

export const growPages = () => [
  { section: "grow", el: <GuidePage /> },
  ...[1, 2, 3].flatMap((m) => [
    { section: "grow", el: <PlanPage m={m} /> },
    { section: "grow", el: <TrackerPage m={m} part={1} /> },
    { section: "grow", el: <TrackerPage m={m} part={2} /> },
  ]),
  { section: "grow", el: <FinalPage /> },
];
